import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import {
  Grid,
  FormControl,
  Autocomplete,
  Button,
  TextField,
  FormHelperText,
  Alert,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'
import { styled } from '@mui/material/styles'
import { lighten, darken } from '@mui/system'

import MainCard from '../common/MainCard'
import {
  SnackbarVariant,
  TypographyVariant,
  ButtonVariant,
} from '../../constants/properties'

import useQueryVehicles from '../../hooks/useQueryVehicles'
import useQueryFuelStores from '../../hooks/useQueryFuelStores'
import useMutationTransferFuel from '../../hooks/useMutationCreateFuelEntry'

import { useUserContext } from '../../contexts/UserContext'
import { FuelRoutes } from '../../utils/routeGenerator'

import Loader from '../../components/common/Loader'
import NoModuleAccess from '../../components/common/NoModuleAccess'

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === 'light'
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8),
}))

const GroupItems = styled('ul')({
  padding: 0,
})

const ActionButtonContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  margin: 35px 0 20px;
  gap: 20px;
`

interface VehicleOption {
  vehicleId: string
  vehicleNo: string
  availableFuel: number
  category: string
}

interface FormValues {
  vehicleId: string
  fuelStoreId: string
  quantity: number | null
}

const initialValues: FormValues = {
  vehicleId: '',
  fuelStoreId: '',
  quantity: null,
}

const TransferFuel = () => {
  const [isDialogOpen, setDialog] = useState(false)
  const [countdown, setCountdown] = useState(3)
  const [countdownActive, setCountdownActive] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { user } = useUserContext()

  const [vehicleOptions, setVehicleOptions] = useState<Array<VehicleOption>>([])
  const [selectedVehicleOption, setSelectedVehicleOption] =
    useState<VehicleOption | null>(null)

  const { data: vehiclesData, isLoading: vehiclesLoading } = useQueryVehicles(
    {}
  )
  const { data: fuelStores, isLoading: fuelStoresLoading } =
    useQueryFuelStores()

  const validationSchema = Yup.object().shape({
    vehicleId: Yup.string().required('Vehicle is required.'),
    fuelStoreId: Yup.string().required('Fuel Store is required.'),
    quantity: Yup.number()
      .required('Quantity is required.')
      .positive('Quantity must be positive.')
      .test(
        'max-quantity',
        'Quantity exceeds available fuel',
        function (value) {
          const { vehicleId } = this.parent
          const vehicle = vehiclesData?.find((v) => v.vehicleId === vehicleId)
          return !vehicle || !value || value <= vehicle.availableFuel
        }
      ),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      transferFuel({
        body: {
          ...values,
          type: 'TRANSFER_TO_STORE',
        },
      })
    },
  })

  const { mutate: transferFuel, isPending: transferFuelPending } =
    useMutationTransferFuel({
      onSuccess: () => {
        enqueueSnackbar('Fuel transferred successfully!', {
          variant: SnackbarVariant.SUCCESS,
        })
        navigate(FuelRoutes.list())
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data.message, {
          variant: SnackbarVariant.ERROR,
        })
      },
    })

  useEffect(() => {
    if (vehiclesData && vehiclesData.length > 0) {
      const vehiclesMapped = vehiclesData
        .filter((vehicle) => vehicle.availableFuel > 0)
        .map(
          ({
            vehicleId,
            basicDetails,
            availableFuel,
          }: {
            vehicleId: string
            availableFuel: number
            basicDetails: {
              vehicleNo: string
              category: string
              name: string
            }
          }) => ({
            vehicleId,
            vehicleNo: basicDetails?.name
              ? `${basicDetails.vehicleNo} (${basicDetails.name})`
              : basicDetails.vehicleNo,
            availableFuel,
            category: basicDetails.category,
          })
        )

      setVehicleOptions(vehiclesMapped)
    }
  }, [vehiclesData])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (countdownActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown, countdownActive])

  const handleOpenDialog = () => {
    setDialog(true)
    setCountdown(3)
    setCountdownActive(true)
  }

  const renderDialogContent = () => {
    const selectedVehicle = vehiclesData?.find(
      (vehicle) => vehicle.vehicleId === formik.values.vehicleId
    )
    const selectedStore = fuelStores?.find(
      (store) => store.fuelStoreId === formik.values.fuelStoreId
    )

    return (
      <>
        <Grid container sx={{ mt: 2 }}>
          <Grid
            xs={12}
            justifyContent="space-between"
            sx={{ display: 'flex', pb: 1 }}
          >
            <Typography variant={TypographyVariant.BODY1} fontWeight={600}>
              From Vehicle:
            </Typography>
            <Typography>
              {selectedVehicle?.basicDetails.vehicleNo}
              {selectedVehicle?.basicDetails.name &&
                ` (${selectedVehicle.basicDetails.name})`}
            </Typography>
          </Grid>
          <Grid
            xs={12}
            justifyContent="space-between"
            sx={{ display: 'flex', pb: 1 }}
          >
            <Typography variant={TypographyVariant.BODY1} fontWeight={600}>
              To Fuel Store:
            </Typography>
            <Typography>{selectedStore?.fuelStoreName}</Typography>
          </Grid>
          <Grid
            xs={12}
            justifyContent="space-between"
            sx={{ display: 'flex', pb: 1 }}
          >
            <Typography variant={TypographyVariant.BODY1} fontWeight={600}>
              Quantity:
            </Typography>
            <Typography>{formik.values.quantity} Ltrs.</Typography>
          </Grid>
        </Grid>
      </>
    )
  }

  if (vehiclesLoading || fuelStoresLoading) {
    return <Loader />
  }

  if (!user?.accessList.fuel) {
    return <NoModuleAccess />
  }

  return (
    <Grid container direction="column">
      <MainCard title="Transfer Fuel" darkTitle>
        <form onSubmit={formik.handleSubmit}>
          <FormControl fullWidth sx={{ mb: 1, mt: 1 }}>
            <Autocomplete
              id="vehicleId"
              options={vehicleOptions.sort(
                (a, b) => -b.category.localeCompare(a.category)
              )}
              groupBy={(option) => option.category}
              getOptionLabel={(option) =>
                `${option.vehicleNo} (${option.availableFuel} Ltrs Avl.)`
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Vehicle (From)" />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )}
              onChange={(e, newValue) => {
                if (newValue) {
                  formik.setFieldValue('vehicleId', newValue.vehicleId)
                  setSelectedVehicleOption(newValue)
                } else {
                  formik.setFieldValue('vehicleId', '')
                  setSelectedVehicleOption(null)
                }
              }}
              onBlur={formik.handleBlur}
              value={selectedVehicleOption}
            />
            {formik.touched.vehicleId && formik.errors.vehicleId ? (
              <FormHelperText error>{formik.errors.vehicleId}</FormHelperText>
            ) : null}
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1, mt: 1 }}>
            <InputLabel id="fuelStoreId-label">
              Select Fuel Store (To)
            </InputLabel>
            <Select
              labelId="fuelStoreId-label"
              id="fuelStoreId"
              name="fuelStoreId"
              label="Select Fuel Store (To)"
              value={formik.values.fuelStoreId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {fuelStores?.map(
                ({ fuelStoreId, fuelStoreName, availableFuel }) => (
                  <MenuItem key={fuelStoreId} value={fuelStoreId}>
                    {`${fuelStoreName} (${availableFuel} Ltrs Avl.)`}
                  </MenuItem>
                )
              )}
            </Select>
            {formik.touched.fuelStoreId && formik.errors.fuelStoreId ? (
              <FormHelperText error>{formik.errors.fuelStoreId}</FormHelperText>
            ) : null}
          </FormControl>

          {selectedVehicleOption && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Available fuel in vehicle:{' '}
              <b>{selectedVehicleOption.availableFuel} Ltrs</b>
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 1, mt: 1 }}>
            <TextField
              label="Quantity (Ltrs)"
              id="quantity"
              name="quantity"
              type="number"
              disabled={!formik.values.vehicleId}
              value={formik.values.quantity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.quantity && Boolean(formik.errors.quantity)}
              helperText={formik.touched.quantity && formik.errors.quantity}
            />
          </FormControl>

          <ActionButtonContainer>
            <Button
              onClick={() => {
                navigate(FuelRoutes.list())
              }}
              variant={ButtonVariant.OUTLINED}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.CONTAINED}
              onClick={handleOpenDialog}
              disabled={
                !formik.isValid ||
                !formik.values.vehicleId ||
                !formik.values.fuelStoreId ||
                !formik.values.quantity
              }
            >
              Transfer Fuel
            </Button>
          </ActionButtonContainer>
        </form>
      </MainCard>

      <Dialog onClose={() => setDialog(false)} open={isDialogOpen} fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm Fuel Transfer
        </DialogTitle>
        <DialogContent>{renderDialogContent()}</DialogContent>
        <DialogActions sx={{ p: 2, gap: 1.5 }}>
          <Button
            onClick={() => {
              setDialog(false)
              setCountdownActive(false)
            }}
            variant={ButtonVariant.OUTLINED}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.CONTAINED}
            disabled={transferFuelPending || (countdownActive && countdown > 0)}
            onClick={() => formik.handleSubmit()}
            color="primary"
            startIcon={
              transferFuelPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {transferFuelPending
              ? 'Processing...'
              : countdownActive && countdown > 0
                ? `Confirm Transfer (${countdown})`
                : 'Confirm Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

export default TransferFuel
